import type { BoxProps } from '@mui/material'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { isSystemTag } from '@tunasong/models'
import type { RenderElementPropsExt } from '@tunasong/plugin-lib'
import { useEditor } from '@tunasong/plugin-lib'
import type { CoreElement } from '@tunasong/schemas'
import { useDeviceUISettings, useMergeRefs } from '@tunasong/ui-lib'
import type { FC, RefObject } from 'react'
import type { DragElementWrapper, DragSourceOptions } from 'react-dnd'
import { useSelected } from 'slate-react'
import { BlockDecorationsBar } from './block-decorations-bar.js'
import { BlockGutter } from './block-gutter.js'
import { HiddenBlock } from './hidden-block.js'

interface DropLineProps {
  position?: 'top' | 'bottom'
}

const DropLine = styled('div', {
  shouldForwardProp: prop => prop !== 'position',
})<DropLineProps>(({ theme, position }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  height: 2,
  opacity: 1,
  background: theme.vars.palette.secondary.main,
  ...(position === 'top' && {
    top: -1,
  }),
  ...(position === 'bottom' && {
    bottom: -1,
  }),
}))

interface StyledBlockOwnProps {
  isInline?: boolean
  hasBorder?: boolean
  hasFocusBorder?: boolean
  hasFocusRightBorder?: boolean
}

const styledBlockOwnPropKeys: Record<keyof StyledBlockOwnProps, true> = {
  isInline: true,
  hasBorder: true,
  hasFocusBorder: true,
  hasFocusRightBorder: true,
}

const StyledBlock = styled(Box, {
  shouldForwardProp: prop => !(prop in styledBlockOwnPropKeys),
})<StyledBlockOwnProps>(({ theme, isInline, hasBorder, hasFocusBorder, hasFocusRightBorder }) => ({
  position: 'relative',
  alignItems: 'center',
  verticalAlign: 'bottom',
  isolation: 'isolate',
  '& ::selection': {
    backgroundColor: theme.vars.palette.primary.light,
    color: theme.vars.palette.primary.contrastText,
  },
  ...(isInline && {
    display: 'inline-flex',
  }),
  ...(hasBorder && {
    borderColor: theme.vars.palette.action.disabled,
    borderRadius: theme.shape.borderRadius,
    borderWidth: theme.spacing(0.1),
    '@media print': {
      border: 'none',
    },
  }),
  ...(hasFocusBorder && {
    transition: 'border-color 0.3s',
    borderColor: theme.vars.palette.primary.main,
    borderRightWidth: theme.spacing(0.5),
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  }),
  ...(hasFocusRightBorder && {
    transition: 'border-color 0.3s',
    border: 'none',
    borderRadius: 0,
    borderWidth: theme.spacing(0.5),
    borderRightStyle: 'solid',
    borderRightColor: theme.vars.palette.primary.main,
  }),
}))

export interface ConfigureBlock {
  prop: string
  validate: (val: unknown) => boolean
  type?: 'text'

  title?: string
}

export interface CoreBlockProps extends RenderElementPropsExt, Omit<BoxProps, 'component' | 'children' | 'border'> {
  className?: string
  component?: 'div' | 'span'
  element: CoreElement
  focus?: boolean
  /** Right border on focus */
  border?: boolean | 'right'
  menu?: boolean

  label?: string

  /** Internal */
  dragRef?: DragElementWrapper<DragSourceOptions>
  blockRef?: RefObject<HTMLDivElement | null>
  dropLine?: 'bottom' | 'left' | 'right' | 'top' | ''
  isDragOver?: boolean
}

/** A Block. Extends Box, so all Box */
export const CoreBlock: FC<CoreBlockProps> = props => {
  const {
    className,
    component = 'div',
    focus = true,
    menu = false,
    border = 'right',
    attributes,
    element,
    label,
    children,
    /** DnD props */
    blockRef,
    dragRef,
    dropLine,
    isDragOver,
    sx,
    ...restProps
  } = props

  const blockSelected = useSelected()
  const editor = useEditor()
  const { type } = element

  const tagsFilter = useDeviceUISettings(state => state.tagsFilter)

  const multiRootRef = useMergeRefs(attributes.ref, blockRef)

  const hasFocus = focus && blockSelected
  const isInline = component === 'span'

  // Hide all tagged blocks (non-system tags) not matching filter if filter is set
  const hiddenByTagsFilter =
    tagsFilter.length > 0 &&
    element.tags?.some(tag => !isSystemTag(tag) && !tagsFilter.includes(tag)) === true

  if (hiddenByTagsFilter) {
    return (
      <HiddenBlock contentEditable={false} text="Hidden by tags filter" {...restProps} attributes={attributes}>
        {children}
      </HiddenBlock>
    )
  }

  return (
    <StyledBlock
      className={className}
      {...restProps}
      isInline={isInline}
      hasBorder={border === true || isDragOver}
      hasFocusBorder={hasFocus && border === true}
      hasFocusRightBorder={hasFocus && border === 'right'}
      sx={sx}
      {...attributes}
      ref={multiRootRef}
      component={component}
    >
      {Boolean(dropLine) && (
        <DropLine
          position={dropLine === 'bottom' ? 'bottom' : dropLine === 'top' ? 'top' : undefined}
          contentEditable={false}
        />
      )}
      {menu && (
        <BlockGutter
          editor={editor}
          element={element}
          blockName={`${type[0].toUpperCase()}${type.slice(1)} Block`}
          hasFocus={hasFocus}
          label={label}
          dragRef={dragRef}
        />
      )}
      <BlockDecorationsBar editor={editor} element={element} />
      {/*
      In Chrome the caret jumps to the end of the contenteditable element when it is the last child of a parent element.
      This is not an issue in Firefox or Safari.

       @see https://stackoverflow.com/questions/27786048/why-is-my-contenteditable-caret-jumping-to-the-end-in-chrome
      */}

      <>{children}</>
    </StyledBlock>
  )
}
